import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, Typography, Divider,
  Modal,
  message,
  InputNumber,
  Table,
  Select,
  Form,
} from 'antd';
import { convertMillisecondsToHours } from 'ontraccr-common/lib/Tasks';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import { getEquipmentHistory, updateEquipment } from './state/equipment.actions';
import { isObjectEmpty } from '../common/helpers';
import EquipmentTimerCard from './EquipmentTimerCard';
import Permissions from '../auth/Permissions';
import {
  formatMillisecondsToHHMM,
  didExceedHoursWarning,
  didExceedMileageWarning,
} from './equipment.helpers';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';
import Debouncer from '../helpers/Debouncer';

import EquipmentUsageTimer from './EquipmentUsageTimer';

import { UNIT_OPTIONS, PROPERTY_MAP, MI_TO_KM } from './equipment.constants';

const { Title, Text } = Typography;

const debouncer = new Debouncer();

export default function EquipmentUsageTab({ equipmentId, isMileage = false }) {
  const dispatch = useDispatch();

  const equipment = useSelector((state) => state.equipment.equipment);
  const projects = useSelector((state) => state.projects.projects);
  const users = useSelector((state) => state.users.users);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const equipmentHistory = useSelector((state) => state.equipment.equipmentHistory);
  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const userMap = useMemo(() => getIdMap(users), [users]);

  const hasWritePerms = Permissions.has('EQUIPMENT_WRITE');
  const {
    thresholdEmailProperty,
    thresholdProperty,
    nearThresholdProperty,
    currentProperty,
    totalLifetimeProperty,
    title,
  } = PROPERTY_MAP[isMileage ? 'mileage' : 'hours'];

  const selectedEquipment = useMemo(() => {
    if (!equipmentId) return null;
    return equipmentMap[equipmentId];
  }, [equipmentId, equipmentMap]);

  const [thresholdValue, setThresholdValue] = useState(selectedEquipment?.[thresholdProperty] || 0);

  useEffect(() => {
    if (equipmentId) dispatch(getEquipmentHistory(equipmentId));
  }, [dispatch, equipmentId]);

  useEffect(() => {
    setThresholdValue(selectedEquipment?.[thresholdProperty] || 0);
  }, [selectedEquipment, thresholdProperty]);

  const nearThresholdValue = useMemo(() => {
    const nearThreshold = selectedEquipment?.[nearThresholdProperty];
    if (nearThreshold !== null && nearThreshold !== undefined) {
      return nearThreshold;
    }
    // Default to 50 less than threshold
    const threshold = selectedEquipment?.[thresholdProperty] || 0;
    return Math.max(0, Number((threshold - 50).toFixed(2)));
  }, [selectedEquipment, nearThresholdProperty, thresholdProperty]);

  const isLive = useMemo(() => (
    !isMileage
    && selectedEquipment?.lastTask?.startTime
    && !selectedEquipment?.lastTask?.endTime
  ), [selectedEquipment, isMileage]);

  const formatTimerData = useCallback((baseMs, task) => {
    if (!selectedEquipment) return 0;
    if (isMileage) return baseMs;

    if (!isObjectEmpty(task ?? {}) && !task.endTime) {
      return {
        currentTask: {
          startTime: task.startTime - baseMs,
          type: 'Check In',
        },
      };
    }
    return formatMillisecondsToHHMM(baseMs);
  }, [selectedEquipment, isMileage]);

  const totalLifetime = useMemo(() => formatTimerData(
    selectedEquipment?.[totalLifetimeProperty] || 0,
    selectedEquipment?.lastTask,
  ), [selectedEquipment, totalLifetimeProperty]);

  const currentSession = useMemo(() => formatTimerData(
    selectedEquipment?.[currentProperty] || 0,
    selectedEquipment?.lastTask,
  ), [selectedEquipment, currentProperty]);

  const handleResetConfirm = useCallback(() => {
    if (!hasWritePerms) return;
    Modal.confirm({
      title: `Reset Current ${title}?`,
      content: `This will set current ${title} to 0. Are you sure?`,
      onOk: async () => {
        try {
          await dispatch(updateEquipment(
            equipmentId,
            {
              [currentProperty]: 0,
              [thresholdEmailProperty]: false,
            },
          ));
        } catch {
          message.error(`Failed to reset current ${title}`);
        }
      },
    });
  }, [equipmentId, hasWritePerms, currentProperty, thresholdEmailProperty, title]);

  const handleUpdateWarningThreshold = useCallback(() => {
    if (!hasWritePerms) return;
    const threshold = selectedEquipment?.[thresholdProperty] || 0;
    const defaultWarningThreshold = Number((threshold - 50).toFixed(2));
    let newWarningThreshold = Math.max(
      0,
      selectedEquipment?.[nearThresholdProperty] ?? defaultWarningThreshold,
    );
    Modal.confirm({
      title: `Update Warning ${title}`,
      content: (
        <InputNumber
          min={0}
          step={isMileage ? 1 : 0.01}
          precision={isMileage ? 0 : 2}
          max={selectedEquipment?.[thresholdProperty] || 0}
          defaultValue={nearThresholdValue}
          onChange={(val) => {
            newWarningThreshold = val;
          }}
          style={{ width: '100%' }}
        />
      ),
      onOk: async () => {
        try {
          await dispatch(updateEquipment(
            equipmentId,
            {
              [nearThresholdProperty]: newWarningThreshold,
            },
          ));
        } catch {
          message.error(`Failed to update warning ${title} threshold`);
        }
      },
    });
  }, [
    equipmentId,
    hasWritePerms,
    nearThresholdProperty,
    nearThresholdValue,
    thresholdProperty,
    selectedEquipment,
    title,
  ]);

  const handleUpdateThreshold = useCallback(() => {
    if (!hasWritePerms) return;
    let newThreshold = selectedEquipment?.[thresholdProperty] || 0;

    Modal.confirm({
      title: `Update Threshold ${title}`,
      content: (
        <InputNumber
          min={selectedEquipment?.[nearThresholdProperty] || 0}
          defaultValue={newThreshold}
          step={isMileage ? 1 : 0.01}
          precision={isMileage ? 0 : 2}
          onChange={(val) => {
            newThreshold = val;
          }}
          style={{ width: '100%' }}
        />
      ),
      onOk: async () => {
        try {
          let current = isMileage
            ? selectedEquipment?.[currentProperty]
            : convertMillisecondsToHours(selectedEquipment?.[currentProperty] || 0);
          if (!isMileage && isLive && selectedEquipment?.lastTask?.startTime) {
            const elapsedMs = Date.now() - (selectedEquipment?.lastTask?.startTime ?? 0);
            const elapsedHours = convertMillisecondsToHours(elapsedMs);
            current += elapsedHours;
          }

          const isExceeded = selectedEquipment?.[thresholdEmailProperty] && current > newThreshold;
          const payload = {
            [thresholdProperty]: newThreshold,
            [thresholdEmailProperty]: !!isExceeded,
          };

          await dispatch(updateEquipment(
            equipmentId,
            payload,
          ));
          setThresholdValue(newThreshold);
        } catch {
          message.error(`Failed to update threshold ${title}`);
        }
      },
    });
  }, [
    equipmentId,
    isLive,
    selectedEquipment,
    hasWritePerms,
    thresholdEmailProperty,
    thresholdProperty,
    nearThresholdProperty,
    currentProperty,
    title,
    isMileage,
  ]);

  const tableColumns = useMemo(() => [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => {
        const displayText = text === 'form' ? 'Form' : 'Check In/Out';
        return (
          <Text style={{ fontWeight: 'bold' }}>
            {displayText}
          </Text>
        );
      },
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      render: (userId) => {
        const user = userMap[userId];
        return (
          <Text style={{ fontWeight: 'bold' }}>
            {user ? `${user.name}` : '-'}
          </Text>
        );
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: isMileage ? 'Mileage' : 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (ms) => {
        let value = '-';
        if (ms != null) {
          if (isMileage) {
            value = ms;
          } else {
            value = formatMillisecondsToHHMM(Number(ms));
          }
        }
        return (
          <Text>
            {value}
          </Text>
        );
      },
    },
  ], [userMap, isMileage]);

  const dataSource = useMemo(() => {
    const currentHistory = equipmentHistory[equipmentId] || [];

    const formattedHistory = currentHistory.map((history) => {
      const {
        type, timestamp, metadata, userId,
      } = history;

      if (type !== `${title} Update`) return null;
      const {
        hours,
        projectId,
        currentLocationText,
        tableType,
        mileage,
      } = metadata || {};
      const date = DateTime.fromMillis(timestamp).toLocaleString({
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const locationText = projectId
        ? formatProjectLabelFromCompanySettings({
          name: projectMap?.[projectId]?.name,
          number: projectMap?.[projectId]?.number,
          settings,
        })
        : currentLocationText;

      return {
        key: history.id,
        type: tableType || type,
        userId,
        location: locationText || '-',
        date,
        hours: isMileage ? mileage : hours,
        timestamp,
      };
    });

    return formattedHistory
      .filter((item) => item !== null)
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [equipmentHistory, equipmentId, projectMap, settings, title, isMileage]);

  const handleUnitChange = useCallback((value) => {
    let newMileage = selectedEquipment?.currentMileage || 0;
    let newThreshold = selectedEquipment?.thresholdMileage || 0;
    let newTotalLifetime = selectedEquipment?.totalLifetimeMileage || 0;
    let newNearThreshold = selectedEquipment?.nearThresholdMileage;

    if (value !== selectedEquipment?.thresholdUnit) {
      // Converting from MI to KM
      if (value === 'km') {
        newMileage *= MI_TO_KM;
        newThreshold *= MI_TO_KM;
        newTotalLifetime *= MI_TO_KM;
        if (newNearThreshold) newNearThreshold *= MI_TO_KM;
      } else {
        // Converting from KM to MI
        newMileage /= MI_TO_KM;
        newThreshold /= MI_TO_KM;
        newTotalLifetime /= MI_TO_KM;
        if (newNearThreshold) newNearThreshold /= MI_TO_KM;
      }
    }

    const payload = {
      thresholdUnit: value,
      currentMileage: parseInt(newMileage, 10),
      thresholdMileage: parseInt(newThreshold, 10),
      totalLifetimeMileage: parseInt(newTotalLifetime, 10),
    };
    if (!isNullOrUndefined(newNearThreshold)) {
      payload.nearThresholdMileage = parseInt(newNearThreshold, 10);
    }

    debouncer.debounce(() => {
      dispatch(updateEquipment(equipmentId, payload));
    }, 100);
  }, [equipmentId, selectedEquipment]);

  const isCheckedIn = useMemo(() => {
    const { lastTask } = selectedEquipment ?? {};
    return lastTask?.startTime && !lastTask?.endTime;
  }, [selectedEquipment]);

  const didExceedWarn = useMemo(() => {
    const warnFunc = isMileage ? didExceedMileageWarning : didExceedHoursWarning;
    return warnFunc({
      [nearThresholdProperty]: selectedEquipment?.[nearThresholdProperty],
      [thresholdProperty]: selectedEquipment?.[thresholdProperty],
      [currentProperty]: selectedEquipment?.[currentProperty],
    });
  }, [
    selectedEquipment,
    nearThresholdProperty,
    thresholdProperty,
    currentProperty,
    isMileage,
  ]);

  let highlightType = null;
  if (selectedEquipment?.[thresholdEmailProperty]) {
    highlightType = 'exceed';
  } else if (didExceedWarn) {
    highlightType = 'near';
  }

  return (
    <>
      <Card style={{ marginBottom: 24 }}>
        { isMileage && (
          <Form.Item label="Unit">
            <Select
              options={UNIT_OPTIONS}
              onChange={handleUnitChange}
              value={selectedEquipment?.thresholdUnit}
              disabled={dataSource?.length}
            />
          </Form.Item>
        )}
        <Row gutter={16} align="middle" justify="space-between">
          <Col xs={24} sm={12} md={6}>
            <EquipmentUsageTimer
              label={`Total Lifetime ${title}`}
              value={totalLifetime}
              isLive={isLive}
              title={title}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <EquipmentUsageTimer
              label={`Current ${title}`}
              value={currentSession}
              isLive={isLive}
              title={title}
              highlightType={highlightType}
              icon={ReloadOutlined}
              onIconClick={
                hasWritePerms && !(isCheckedIn && !isMileage)
                  ? handleResetConfirm
                  : null
              }
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <EquipmentUsageTimer
              label={`Threshold ${title}`}
              value={thresholdValue}
              isLive={isLive}
              title={title}
              icon={EditOutlined}
              onIconClick={hasWritePerms ? handleUpdateThreshold : null}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <EquipmentUsageTimer
              label={`Warning ${title}`}
              value={nearThresholdValue}
              isLive={isLive}
              title={title}
              icon={EditOutlined}
              onIconClick={hasWritePerms ? handleUpdateWarningThreshold : null}
            />
          </Col>
        </Row>
      </Card>
      <Divider />
      { !isMileage && (
        <Row align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <Title level={4} style={{ margin: 0 }}>Current Check-In</Title>
              <EquipmentTimerCard
                task={selectedEquipment?.lastTask}
                style={{ width: 400 }}
              />
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col span={24}>
          <Title level={4}>{title} History</Title>
          <Table
            dataSource={dataSource}
            columns={tableColumns}
            size="small"
            pagination={false}
            scroll
            bordered
          />
        </Col>
      </Row>
    </>
  );
}

EquipmentUsageTab.propTypes = {
  equipmentId: PropTypes.string,
  isMileage: PropTypes.bool,
};

EquipmentUsageTab.defaultProps = {
  equipmentId: null,
  isMileage: false,
};
